import { useEffect, useRef, useState } from 'react';
import { Bell, Check, ChevronRight, Clock3, FolderKanban, GitBranch, Pause, Play, ShieldCheck, Workflow } from 'lucide-react';
import './CapabilityShowcase.css';

const SECTIONS = [
    { title: 'Planning', icon: GitBranch, description: 'Give the next release a clear path.', items: ['Critical path and Gantt timeline', 'Phases and milestones', 'Baselines and planned-versus-actual tracking', 'Project history at a point in time'] },
    { title: 'Monitoring to delivery', icon: Workflow, description: 'Follow an issue through to a human fix check.', items: ['Sentry project discovery and shared connections', 'Signed generic monitoring webhooks', 'Opt-in task rules and duplicate suppression', 'Authenticated GitHub PR and check events', 'Deployment evidence and human fix checks per environment', 'Regression review'] },
    { title: 'Time', icon: Clock3, description: 'See where the work goes.', items: ['Time entries and timers', 'Recurring tasks'] },
    { title: 'Project context', icon: FolderKanban, description: 'Keep the decisions next to the task.', items: ['Task comments and attachments', 'Commit and pull request links', 'Custom fields and saved views', 'Client, app, and system records linked to tasks'] },
    { title: 'Staying informed', icon: Bell, description: 'Start the day with the work that needs you.', items: ['In-app notifications', 'Daily briefing', 'Read-only Google Calendar or iCloud calendar sync for the briefing, where enabled'] },
    { title: 'Workspace control', icon: ShieldCheck, description: 'Give each person the right access.', items: ['Roles and permissions', 'Workspace billing through Stripe', 'Installable web app'] },
];
const DURATION = 8000;

export function CapabilityShowcase() {
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [visible, setVisible] = useState(false);
    const [pageVisible, setPageVisible] = useState(!document.hidden);
    const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    const root = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const onMotionChange = () => setReducedMotion(query?.matches ?? false);
        query?.addEventListener('change', onMotionChange);
        const onVisibilityChange = () => setPageVisible(!document.hidden);
        document.addEventListener('visibilitychange', onVisibilityChange);
        const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(
            ([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 },
        );
        if (root.current) observer?.observe(root.current);
        return () => {
            query?.removeEventListener('change', onMotionChange);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            observer?.disconnect();
        };
    }, []);

    const playing = !paused && !hovered && !focused && !reducedMotion && visible && pageVisible;
    useEffect(() => {
        if (!playing) return;
        const timer = window.setTimeout(() => setActive(i => (i + 1) % SECTIONS.length), DURATION);
        return () => window.clearTimeout(timer);
    }, [playing, active]);

    const section = SECTIONS[active];
    return (
        <div ref={root} className="capability-showcase"
            onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
            onFocus={() => setFocused(true)}
            onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
            <div className="capability-controls">
                <div className="capability-choices" role="group" aria-label="Delivery capabilities">
                    {SECTIONS.map((item, index) => (
                        <button key={item.title} type="button" aria-pressed={active === index} aria-controls="capability-panel"
                            onClick={() => { setActive(index); setPaused(true); }} className="capability-choice">
                            <item.icon size={20} aria-hidden="true" />
                            <span>{item.title}</span>
                            <ChevronRight size={16} aria-hidden="true" className="capability-chevron" />
                        </button>
                    ))}
                </div>
                {!reducedMotion && <button type="button" className="capability-playback" onClick={() => setPaused(value => !value)}
                    aria-label={paused ? 'Play capability tour' : 'Pause capability tour'}>
                    {paused ? <Play size={16} aria-hidden="true" /> : <Pause size={16} aria-hidden="true" />}
                    {paused ? 'Play tour' : 'Pause tour'}
                </button>}
            </div>
            <div id="capability-panel" className="capability-panel" role="region" aria-label={section.title}>
                <div key={active} className="capability-scene">
                    <div className="capability-scene-heading">
                        <span className="capability-eyebrow">{String(active + 1).padStart(2, '0')} / 06</span>
                        <span className="capability-example-label">Illustrative example</span>
                    </div>
                    <h3>{section.description}</h3>
                    <CapabilityIllustration active={active} />
                    <ul className="capability-features">
                        {section.items.map((item, index) => <li key={item} style={{ animationDelay: `${index * 65}ms` }}><Check size={16} aria-hidden="true" /><span>{item}</span></li>)}
                    </ul>
                </div>
                <div className="capability-progress" aria-hidden="true">
                    <span key={`${active}-${playing}`} className={playing ? 'is-playing' : ''} />
                </div>
            </div>
        </div>
    );
}

function CapabilityIllustration({ active }: { active: number }) {
    if (active === 0) return (
        <div className="capability-demo" aria-label="Example release timeline">
            <div className="capability-demo-title"><span>Next release</span><span>Week 01 → 04</span></div>
            {['Design', 'Build', 'Verify'].map((label, index) => <div className="capability-timeline-row" key={label}>
                <span>{label}</span><div className="capability-timeline-track"><span style={{ marginLeft: `${index * 27}%`, width: `${index === 1 ? 43 : 30}%`, animationDelay: `${index * 140}ms` }} /></div>
            </div>)}
            <div className="capability-demo-note"><GitBranch size={14} aria-hidden="true" /> Dependencies keep the plan connected</div>
        </div>
    );
    if (active === 1) return (
        <div className="capability-demo" aria-label="Example issue delivery history">
            <div className="capability-demo-title"><span>Fix checkout retries</span><span>PR #42</span></div>
            <div className="capability-delivery-flow">{['Issue', 'Pull request', 'Deploy', 'Human check'].map((label, index) => <div key={label} style={{ animationDelay: `${index * 170}ms` }}><span>{index + 1}</span>{label}</div>)}</div>
            <div className="capability-demo-note"><ShieldCheck size={14} aria-hidden="true" /> Your team decides when the task is done</div>
        </div>
    );
    if (active === 2) return (
        <div className="capability-demo" aria-label="Example time entries">
            <div className="capability-demo-title"><span>Time on the release</span><Clock3 size={16} aria-hidden="true" /></div>
            {['Implementation', 'Code review', 'Verification'].map((label, index) => <div className="capability-time-row" key={label}><span>{label}</span><div><span style={{ width: `${85 - index * 25}%`, animationDelay: `${index * 140}ms` }} /></div><strong>{['2h 30m', '1h 15m', '45m'][index]}</strong></div>)}
        </div>
    );
    if (active === 3) return (
        <div className="capability-demo" aria-label="Example task context">
            <div className="capability-demo-title"><span>Retry failed webhooks</span><span>In review</span></div>
            <div className="capability-context-tags"><span>Billing service</span><span>PR #42</span><span>retry-spec.md</span></div>
            <div className="capability-quote"><span className="capability-avatar">M</span><p><strong>Maya</strong><br />Keep the retry limit at three and log each attempt.</p></div>
        </div>
    );
    if (active === 4) return (
        <div className="capability-demo" aria-label="Example daily briefing">
            <div className="capability-demo-title"><span>Your daily briefing</span><Bell size={16} aria-hidden="true" /></div>
            {[['Due today', 'Review the checkout fix'], ['Mention', 'Maya mentioned you in a task'], ['Calendar', 'Release check-in · 10:00']].map(([label, detail], index) => <div className="capability-briefing-row" key={label} style={{ animationDelay: `${index * 140}ms` }}><span>{label}</span><strong>{detail}</strong></div>)}
        </div>
    );
    return (
        <div className="capability-demo" aria-label="Example workspace roles">
            <div className="capability-demo-title"><span>Workspace members</span><ShieldCheck size={16} aria-hidden="true" /></div>
            {[['Maya', 'Admin'], ['Alex', 'Member'], ['Sam', 'Viewer']].map(([name, role], index) => <div className="capability-member-row" key={name} style={{ animationDelay: `${index * 140}ms` }}><span className="capability-avatar">{name[0]}</span><strong>{name}</strong><span className="capability-role">{role}</span></div>)}
        </div>
    );
}
